import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import { SNETTextfield, AlertBox, alertTypes, TagsInputField } from "shared/dist/components";
import validator from "shared/dist/utils/validator";
import {
    servicePricingValidationConstraints,
    daemonValidationConstraints,
} from "../../validationConstraints";
import { agiToCogs } from "shared/dist/utils/Pricing";
import { cogsToAgi } from "shared/dist/utils/Pricing";
import BigNumber from "bignumber.js";

import { DECIMALS } from "shared/src/utils/Pricing";
import { TextField } from "@mui/material";
import classes from "./Region.module.css";
import { setAiServiceGroups } from "../../../../../Services/Redux/actionCreators/aiServiceDetailsActions";
import { updatePrimaryServiceGroup } from "../../../../../Utils/serviceGroups";

const Region = ({ isFreezedByActivatedHaas, isHaasDaemonInitiated }) => {
    const dispatch = useDispatch();

    const serviceGroups = useSelector((state) => state.aiServiceDetails.groups);
    const isHaaSUsed = useSelector((state) => state.aiServiceDetails.isHaaSUsed);
    const orgGroups = useSelector((state) => state.organization.groups);

    const [freeCallsValidation, setfreeCallsValidation] = useState();
    const [freeCallAddressValidation, setFreeCallAddressValidation] = useState();
    const [priceValidation, setPriceValidation] = useState();
    const [daemonAddressValidation, setDaemonAddressValidation] = useState();
    const [daemonEndpointValidation, setDaemonEndpointValidation] = useState();
    const [sertificateNotFound, setSertificateNotFound] = useState();

    const selectedServiceGroup = serviceGroups[0];
    const selectedServicePricing = selectedServiceGroup.pricing
        ? selectedServiceGroup.pricing[0]
        : {};

    const handleValidation = (isInvalid, setterValidation, errorMessage = isInvalid) => {
        if (isInvalid) {
            setterValidation({ type: alertTypes.ERROR, message: errorMessage });
            return false;
        }
        setterValidation();
        return true;
    };

    const handleEndPointValidation = (value) => {
        const invalidEndpointMessage = validator.validators.validDaemonEndpoint(value, {
            message: `${value} is not a valid endpoint`,
        });

        setSertificateNotFound();
        return handleValidation(invalidEndpointMessage, setDaemonEndpointValidation);
    };

    const validateDaemonAddress = (value) => {
        const invalidDaemonAddressMessage = validator.single(value, daemonValidationConstraints);
        return handleValidation(
            invalidDaemonAddressMessage,
            setDaemonAddressValidation,
            `${value} is not a valid daemon address`
        );
    };

    const validateFreeCallAddress = (value) => {
        const invalidFreeCallAddressMessage = validator.single(value, daemonValidationConstraints);
        return handleValidation(
            invalidFreeCallAddressMessage,
            setFreeCallAddressValidation,
            `${value} is not a valid address`
        );
    };

    const handleFreeCallsValidation = (value) => {
        const isNotValid = validator.single(
            value,
            servicePricingValidationConstraints.freeCallsAllowed
        );
        handleValidation(isNotValid, setfreeCallsValidation, `${value} is not a valid`);
    };

    const handlePriceValidation = (value) => {
        const oneCogsToAgi = cogsToAgi(1);
        const isNotValid = value < oneCogsToAgi && !isDecimalPlacesExceeds(value) && !isNaN(+value);
        handleValidation(
            isNotValid,
            setPriceValidation,
            `Price of the service should be greater than or equal to ${oneCogsToAgi} ${process.env.REACT_APP_TOKEN_NAME}.`
        );
    };

    const handleNewEndpointsChange = (newEndpoints) => {
        let updatedEndpoints = {};
        newEndpoints.forEach((endpoint) => {
            if (handleEndPointValidation(endpoint)) {
                updatedEndpoints = {
                    ...updatedEndpoints,
                    [endpoint]: { ...selectedServiceGroup.endpoints?.[endpoint], valid: false },
                };
            }
        });
        updateServiceGroups("endpoints", updatedEndpoints);
        // if (isEmpty(updatedServiceGroups[0].testEndpoints)) {
        //     const endpoint = Object.keys(updatedServiceGroups[0].endpoints)[0];
        //     updatedServiceGroups[0].testEndpoints = [endpoint];
        // }
    };

    const parseEndpoints = () => {
        const parsedEndpointsArray = [];
        if (selectedServiceGroup.endpoints) {
            Object.entries(selectedServiceGroup.endpoints).forEach(([item]) =>
                parsedEndpointsArray.push(item)
            );
        }
        return parsedEndpointsArray;
    };

    const handleNewDaemonAddressChange = (newAddresses) => {
        let updatedAddresses = [];
        newAddresses.forEach((address) => {
            if (validateDaemonAddress(address)) {
                updatedAddresses.push(address);
            }
        });
        updateServiceGroups("daemonAddresses", updatedAddresses);
    };

    const updateServiceGroups = (name, value) => {
        const updatedGroups = updatePrimaryServiceGroup({
            serviceGroups,
            orgGroups,
            patch: { [name]: value },
        });
        dispatch(setAiServiceGroups(updatedGroups));
    };

    const handleFreeCallsChange = (event) => {
        const { value } = event.target;
        handleFreeCallsValidation(value);
        updateServiceGroups("freeCallsAllowed", value);
    };

    const isDecimalPlacesExceeds = (value) => {
        return new BigNumber(value).decimalPlaces() > DECIMALS;
    };

    const handlePriceChange = (event) => {
        let { value } = event.target;
        handlePriceValidation(value);
        value = agiToCogs(value);
        const updatedServicePricing = [...selectedServiceGroup.pricing];
        updatedServicePricing[0] = { ...selectedServicePricing, priceInCogs: value };
        updateServiceGroups("pricing", updatedServicePricing);
    };

    const handleFreeCallAddress = (event) => {
        const { value } = event.target;
        validateFreeCallAddress(value);
        updateServiceGroups("freeCallSignerAddress", value);
    };

    const daemonEndpoint = useMemo(
        () => Object.keys(selectedServiceGroup.endpoints)[0],
        [selectedServiceGroup.endpoints]
    );

    return (
        <>
            <div className={classes.row}>
                <div className={classes.servicePriceModelContainer}>
                    <SNETTextfield
                        infoMsg={`Price in ${process.env.REACT_APP_TOKEN_NAME}`}
                        name="price"
                        defaultValue={
                            selectedServicePricing && cogsToAgi(selectedServicePricing.priceInCogs)
                        }
                        label="AI Service Price"
                        onChange={handlePriceChange}
                        error={priceValidation}
                        required
                        disabled={isFreezedByActivatedHaas}
                    />
                    <SNETTextfield
                        infoMsg="Number of free calls per user"
                        name="freeCallsAllowed"
                        value={selectedServiceGroup.freeCallsAllowed}
                        label="Demo Free Calls"
                        onChange={handleFreeCallsChange}
                        error={freeCallsValidation}
                        required
                        disabled={isFreezedByActivatedHaas}
                    />
                </div>
                {isHaaSUsed ? (
                    <TextField
                        name="daemonEndpoint"
                        value={daemonEndpoint ?? " "}
                        label="Daemon Endpoint"
                        disabled
                    />
                ) : (
                    <TagsInputField
                        name="endpoints"
                        addTags={handleNewEndpointsChange}
                        label="Daemon Endpoints"
                        infoMsg="Enter all the public Daemon end points that will be used to call the service"
                        tagsEntered={parseEndpoints()}
                        additionalInfo="You can add up to 20 endpoints"
                        error={daemonEndpointValidation}
                        required
                        disabled={isFreezedByActivatedHaas || isHaasDaemonInitiated}
                    />
                )}
                {sertificateNotFound && (
                    <div className={classes.endpointContainer}>
                        <AlertBox {...sertificateNotFound} />
                    </div>
                )}
            </div>
            <div className={classes.divider} />
            <div className={classes.row}>
                <span className={isFreezedByActivatedHaas ? classes.disabled : undefined}>
                    <p>
                        These Ethereum addresses are required for your Daemon to sign outgoing
                        requests
                    </p>
                    <a href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/DevelopersTutorials/FullGuideOnboarding/#enabling-metering-and-free-calls">
                        How to generate these addresses
                    </a>
                </span>
                <div className={clsx(classes.row, classes.subRow)}>
                    {isHaaSUsed ? (
                        <TextField
                            name="signerAddress"
                            value={process.env.REACT_APP_HAAS_FREECALL_ADDRESS}
                            label="HaaS Free Call Signer Address"
                            disabled
                        />
                    ) : (
                        <SNETTextfield
                            name="signerAddress"
                            value={selectedServiceGroup.freeCallSignerAddress ?? ""}
                            label="Free Call Signer Address"
                            placeholder="Free Call Signer Address"
                            onChange={handleFreeCallAddress}
                            error={freeCallAddressValidation}
                            infoMsg="Free Call Signer Address is used to sign and verify free call requests from
                        users. Without this address, free calls won’t be available for your service"
                            required={true}
                            disabled={isFreezedByActivatedHaas || isHaasDaemonInitiated}
                        />
                    )}
                </div>
                <div className={clsx(classes.row, classes.subRow)}>
                    <TagsInputField
                        name="daemonAdresses"
                        addTags={handleNewDaemonAddressChange}
                        label="Metering Addresses"
                        tagsEntered={selectedServiceGroup.daemonAddresses}
                        additionalInfo="You can add up to 20 addresses"
                        infoMsg="Metering Address is used when your Daemon sends usage data (metering stats)
                        to the SingularityNET platform. Signing this data proves that it comes from
                        your service and hasn’t been tampered with"
                        error={daemonAddressValidation}
                        required={true}
                        disabled={isFreezedByActivatedHaas}
                    />
                </div>
            </div>
        </>
    );
};

export default Region;
